"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function Legal() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const termsData = [{
    id: "item-1",
    title: "1. Definitions",
    content: "In these Terms and Conditions, 'Company' refers to Coastline Air Conditioning Services Ltd. 'Client' refers to the individual or entity requesting services. 'Services' includes installation, maintenance, repair, and consultation regarding air conditioning and climate control systems."
  }, {
    id: "item-2",
    title: "2. Scope of Services",
    content: "Coastline Air Conditioning Services Ltd provides professional HVAC solutions including design, supply, installation, and ongoing maintenance. All work is carried out by F-Gas certified engineers in accordance with UK building regulations and environmental standards."
  }, {
    id: "item-3",
    title: "3. Pricing and Quotations",
    content: "Quotations are valid for 30 days from the date of issue. Prices are subject to change if the scope of work is modified by the Client or if unforeseen site conditions are discovered during the project execution."
  }, {
    id: "item-4",
    title: "4. Payment Terms",
    content: "For installation projects, a deposit may be required prior to commencement. Final payment is due within 14 days of invoice date unless otherwise agreed in writing. Late payments may incur interest charges at the statutory rate."
  }, {
    id: "item-5",
    title: "5. Installation Terms",
    content: "The Client must ensure clear access to the site and provide necessary utilities (electricity/water). Coastline Air Conditioning Services Ltd is not responsible for structural defects in the building that affect installation unless caused by our negligence."
  }, {
    id: "item-6",
    title: "6. Maintenance Terms",
    content: "Maintenance contracts are subject to annual renewal. Scheduled visits will be coordinated in advance. Emergency call-outs outside of contract hours may be subject to additional premium rates."
  }, {
    id: "item-7",
    title: "7. Warranty",
    content: "New equipment is covered by the manufacturer's warranty. Coastline Air Conditioning Services Ltd provides a 12-month workmanship warranty on all installations, provided the system is maintained according to manufacturer guidelines."
  }, {
    id: "item-8",
    title: "8. Liability Limitations",
    content: "Coastline Air Conditioning Services Ltd shall not be liable for indirect or consequential losses. Our total liability for any claim shall not exceed the total amount paid by the Client for the specific service provided."
  }, {
    id: "item-9",
    title: "9. Intellectual Property",
    content: "All designs, drawings, and technical specifications provided by Coastline Air Conditioning Services Ltd remain our intellectual property and may not be reproduced without written consent."
  }, {
    id: "item-10",
    title: "10. Confidentiality",
    content: "Both parties agree to keep confidential any proprietary information shared during the course of the business relationship, including pricing structures and technical methodologies."
  }, {
    id: "item-11",
    title: "11. Termination",
    content: "Either party may terminate a service agreement with 30 days' written notice. The Client remains liable for all work completed and materials ordered up to the date of termination."
  }, {
    id: "item-12",
    title: "12. Dispute Resolution",
    content: "Any disputes arising from these terms shall first be attempted to be resolved through good-faith negotiation. If unresolved, the matter shall be referred to independent mediation."
  }, {
    id: "item-13",
    title: "13. Changes to Terms",
    content: "Coastline Air Conditioning Services Ltd reserves the right to update these Terms and Conditions. The most current version will always be available on our website and applies to all new contracts."
  }, {
    id: "item-14",
    title: "14. Governing Law",
    content: "These Terms and Conditions are governed by and construed in accordance with the laws of England and Wales. Any legal proceedings shall be subject to the exclusive jurisdiction of the English courts."
  }];
  return <section data-section-id="2627" id="legaldisclaimers" className="bg-background text-foreground py-40 px-6 md:px-12 lg:px-16 relative overflow-hidden">
      <Separator className="bg-border mb-12" />

      <div className="mb-20 md:mb-24">
        <span className="font-mono text-[11px] tracking-widest text-primary uppercase">
          02 / Terms & Conditions
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 relative">
        <div className="hidden lg:block absolute pointer-events-none" style={{
        left: "calc(41.666% - 12px)",
        top: "40%"
      }} aria-hidden="true">
          <div className="relative w-6 h-6 opacity-30">
            <div className="absolute top-1/2 left-0 w-full h-px bg-primary" />
            <div className="absolute top-0 left-1/2 h-full w-px bg-primary" />
          </div>
        </div>

        <div ref={headingRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default font-bold leading-[0.9] text-foreground" style={{
          fontSize: "clamp(3.5rem, 9vw, 6rem)",
          letterSpacing: "-0.03em"
        }}>
            <span className="block">LEGAL</span>
            <span className="block">TERMS</span>
            <span className="block text-muted-foreground">/</span>
            <span className="block">SERVICE</span>
            <span className="block relative inline-block w-fit">
              POLICIES
              <span className="absolute left-0 right-0 bottom-2 h-3 -z-0" style={{
              backgroundColor: "var(--primary)",
              opacity: 0.85,
              mixBlendMode: "normal"
            }} aria-hidden="true" />
            </span>
          </h2>
        </div>

        <div ref={contentRef} className={`lg:col-span-7 lg:pt-20 transition-all duration-1000 ease-out delay-150 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {termsData.map((term, i) => <AccordionItem key={term.id} value={term.id} data-index={i} className="border border-border rounded-lg px-4 bg-card/50 backdrop-blur-sm hover:border-primary transition-colors">
                <AccordionTrigger className="text-left font-bold text-lg py-6 hover:no-underline hover:text-primary transition-colors">
                  {term.title}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-6 text-base">
                  {term.content}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}